import { useEffect, useState } from "react";
import api, { windowLabel, WINDOWS, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, Trash2, Users, Repeat, Star, MapPin, Download, Settings as SettingsIcon, Save, Send, MessageSquare, Megaphone, Mail, CheckCircle, X as XIcon, Shield, UserPlus, KeyRound, Camera, FileText, Flag, TrendingUp, Truck, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const BASE_TABS = [
  { k: "runsheet", label: "Run Sheet" },
  { k: "orders", label: "Orders" },
  { k: "money", label: "Money" },
  { k: "subscriptions", label: "Subscriptions" },
  { k: "birthdays", label: "Birthday Support" },
  { k: "workers", label: "Workers" },
  { k: "stats", label: "Stats" },
  { k: "routes", label: "Route List" },
  { k: "customers", label: "Customers" },
  { k: "referrals", label: "Referrals" },
  { k: "announcements", label: "Announcements" },
  { k: "areas", label: "Service Areas" },
  { k: "reviews", label: "Reviews" },
  { k: "contacts", label: "Inbox" },
  { k: "waitlist", label: "Waitlist" },
  { k: "broadcast", label: "Broadcast" },
  { k: "contests", label: "Contests" },
  { k: "mystery", label: "Mystery Drops" },
  { k: "settings", label: "Settings" },
];
const STAFF_TAB = { k: "staff", label: "Staff" };

export default function AdminDashboard() {
  const [tab, setTab] = useState("orders");
  const { user } = useAuth();
  const canManageStaff = user && ["superadmin", "parent"].includes(user.role);
  const TABS = canManageStaff ? [...BASE_TABS, STAFF_TAB] : BASE_TABS;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900" data-testid="admin-title">Parent Dashboard</h1>
        <p className="text-slate-600 mt-1">Manage orders, routes, customers, and settings.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b-2 border-slate-100 pb-3">
        {TABS.map(({ k, label }) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-xl text-sm font-semibold ${tab === k ? "bg-slate-800 text-white" : "bg-white border-2 border-slate-200 text-slate-700"}`} data-testid={`admintab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "runsheet" && <RunSheetTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "money" && <MoneyTab />}
      {tab === "subscriptions" && <SubscriptionsTab />}
      {tab === "birthdays" && <BirthdaySupportTab />}
      {tab === "workers" && <WorkersTab />}
      {tab === "stats" && <StatsTab />}
      {tab === "routes" && <RoutesTab />}
      {tab === "customers" && <CustomersTab />}
      {tab === "referrals" && <ReferralsTab />}
      {tab === "announcements" && <AnnouncementsTab />}
      {tab === "areas" && <AreasTab />}
      {tab === "reviews" && <ReviewsTab />}
      {tab === "contacts" && <ContactsTab />}
      {tab === "waitlist" && <WaitlistTab />}
      {tab === "broadcast" && <BroadcastTab />}
      {tab === "contests" && <ContestsTab />}
      {tab === "mystery" && <MysteryTab />}
      {tab === "settings" && <SettingsTab />}
      {tab === "staff" && canManageStaff && <StaffTab actorRole={user.role}/>}
    </div>
  );
}

function StatsTab() {
  const [s, setS] = useState(null);
  const [snap, setSnap] = useState(null);
  const [chart, setChart] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
    api.get("/admin/revenue/snapshot").then((r) => setSnap(r.data));
    api.get("/admin/revenue-chart?days=30").then((r) => setChart(r.data));
  }, []);
  if (!s || !snap) return <div className="text-slate-500">Loading…</div>;
  const snapCards = [
    { label: "Today's Earnings", val: `$${snap.today_earnings.toFixed(2)}`, icon: <DollarSign size={20}/> },
    { label: "This Week", val: `$${snap.weekly_revenue.toFixed(2)}`, icon: <TrendingUp size={20}/> },
    { label: "This Month", val: `$${snap.monthly_revenue.toFixed(2)}`, icon: <TrendingUp size={20}/> },
    { label: "Active Customers", val: snap.active_customers, icon: <Users size={20}/> },
    { label: "New Signups (wk)", val: snap.new_signups_week, icon: <UserPlus size={20}/> },
    { label: "Referrals (wk)", val: snap.referrals_week, icon: <Star size={20}/> },
  ];
  const totalCards = [
    { label: "Total Revenue", val: `$${s.total_revenue.toFixed(2)}`, icon: <DollarSign size={20}/> },
    { label: "Total Tips", val: `$${s.total_tips.toFixed(2)}`, icon: <Star size={20}/> },
    { label: "Cleaning Revenue", val: `$${s.total_cleaning_revenue.toFixed(2)}`, icon: <Star size={20}/> },
    { label: "Total Pickups", val: s.total_pickups, icon: <Trash2 size={20}/> },
    { label: "Total Bags", val: s.total_bags, icon: <Trash2 size={20}/> },
    { label: "Active Recurring", val: s.recurring_subscriptions, icon: <Repeat size={20}/> },
    { label: "Loyal Customers", val: s.loyal_customers, icon: <Users size={20}/> },
  ];
  return (
    <div data-testid="admin-stats">
      <h3 className="font-fredoka font-bold text-lg mb-2">Snapshot</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {snapCards.map(({ label, val, icon }) => (
          <div key={label} className="card-clay p-5">
            <div className="flex items-center gap-2 text-slate-500"><div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">{icon}</div><div className="text-xs font-bold uppercase tracking-wider">{label}</div></div>
            <div className="font-fredoka text-3xl font-bold text-slate-900 mt-2">{val}</div>
          </div>
        ))}
      </div>

      <h3 className="font-fredoka font-bold text-lg mb-2 mt-6">Revenue — last 30 days</h3>
      <div className="card-clay p-5 mb-4" data-testid="revenue-chart">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd"/>
            <YAxis tick={{ fontSize: 11 }} width={40}/>
            <Tooltip contentStyle={{ borderRadius: 12, border: "2px solid #e2e8f0" }}/>
            <Line type="monotone" dataKey="revenue" stroke="#16a34a" strokeWidth={3} dot={{ r: 3 }}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      <h3 className="font-fredoka font-bold text-lg mb-2 mt-6">All-time totals</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {totalCards.map(({ label, val, icon }) => (
          <div key={label} className="card-clay p-5">
            <div className="flex items-center gap-2 text-slate-500"><div className="w-9 h-9 rounded-xl bg-green-100 text-green-700 flex items-center justify-center">{icon}</div><div className="text-xs font-bold uppercase tracking-wider">{label}</div></div>
            <div className="font-fredoka text-3xl font-bold text-slate-900 mt-2">{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState({ window: "", payment_status: "", status: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const q = Object.entries(filter).filter(([_, v]) => v).map(([k, v]) => `${k}=${v}`).join("&");
    const { data } = await api.get(`/admin/orders${q ? "?" + q : ""}`);
    setOrders(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const markPaid = async (id) => {
    try {
      await api.put(`/admin/orders/${id}/mark-paid`, {});
      toast.success("Marked paid");
      load();
    } catch { toast.error("Failed"); }
  };

  const addNote = async (id) => {
    const note = window.prompt("Internal note:");
    if (!note) return;
    await api.put(`/admin/orders/${id}`, { status: "scheduled", admin_note: note });
    toast.success("Note added");
    load();
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("bd_token");
    const res = await fetch(`${BACKEND_URL}/api/admin/orders/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "orders.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Select label="Window" value={filter.window} onChange={(v) => setFilter({ ...filter, window: v })} options={[["", "All windows"], ...WINDOWS.map(w => [w.value, w.label])]} testid="filter-window"/>
        <Select label="Payment" value={filter.payment_status} onChange={(v) => setFilter({ ...filter, payment_status: v })} options={[["", "All"], ["pending", "Pending"], ["paid", "Paid"], ["failed", "Failed"], ["refunded", "Refunded"], ["canceled", "Canceled"]]} testid="filter-payment"/>
        <Select label="Status" value={filter.status} onChange={(v) => setFilter({ ...filter, status: v })} options={[["", "All"], ["scheduled", "Scheduled"], ["picked_up", "Picked up"], ["completed", "Completed"], ["skipped", "Skipped"], ["canceled", "Canceled"]]} testid="filter-status"/>
        <button onClick={exportCsv} className="btn-push-outline px-4 py-2 text-sm inline-flex items-center gap-2 ml-auto" data-testid="export-csv"><Download size={14}/> Export CSV</button>
      </div>

      {loading ? <div className="text-slate-500">Loading…</div> : (
        <div className="card-clay overflow-x-auto" data-testid="orders-table">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Where</th>
                <th className="px-4 py-3">Bags</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-50">
                  <td className="px-4 py-3">{windowLabel(o.window)}<div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()}</div></td>
                  <td className="px-4 py-3"><div className="font-semibold">{o.name}</div><div className="text-xs text-slate-500">{o.phone}</div></td>
                  <td className="px-4 py-3">{o.community}<div className="text-xs text-slate-500">Bldg {o.building} · #{o.unit}</div></td>
                  <td className="px-4 py-3 font-semibold">{o.bags}{o.monthly_cleaning ? " + clean" : ""}</td>
                  <td className="px-4 py-3 font-semibold">${o.total_amount.toFixed(2)}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-700"}`}>{o.payment_status}</span></td>
                  <td className="px-4 py-3"><span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">{o.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <ActionBtn onClick={() => setStatus(o.id, "picked_up")} testid={`pick-${o.id}`}>Pick up</ActionBtn>
                      <ActionBtn onClick={() => setStatus(o.id, "completed")} testid={`done-${o.id}`}>Done</ActionBtn>
                      <ActionBtn onClick={() => setStatus(o.id, "skipped")} testid={`skip-${o.id}`}>Skip</ActionBtn>
                      <ActionBtn onClick={() => setStatus(o.id, "canceled")} testid={`cancel-${o.id}`}>Cancel</ActionBtn>
                      <ActionBtn onClick={() => addNote(o.id)} testid={`note-${o.id}`}>Note</ActionBtn>
                      {o.payment_status !== "paid" && (
                        <ActionBtn onClick={() => markPaid(o.id)} testid={`markpaid-${o.id}`}>Mark Paid</ActionBtn>
                      )}
                    </div>
                    {o.admin_notes && o.admin_notes.length > 0 && (
                      <div className="text-xs text-slate-500 mt-1">{o.admin_notes.length} note(s)</div>
                    )}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">No orders found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Window-of-day order so morning windows show before evening
const WINDOW_TOD = { fri_am: 0, sat_am: 1, fri_pm: 2, sat_pm: 3 };

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextFriSatDates(count = 8) {
  const out = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (out.length < count) {
    const dow = d.getDay(); // 0=Sun..6=Sat
    if (dow === 5 || dow === 6) out.push(toISODate(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

function RoutesTab() {
  const [selectedDate, setSelectedDate] = useState(null); // null => today
  const [route, setRoute] = useState({ date: "", is_today: true, items: [] });
  const [loading, setLoading] = useState(true);
  const [winFilter, setWinFilter] = useState("all");
  const [workers, setWorkers] = useState([]);
  const [stats, setStats] = useState({ workers: [], unassigned: 0 });
  const [bulkWorker, setBulkWorker] = useState("");
  const [bulkWindow, setBulkWindow] = useState("all");
  const [busy, setBusy] = useState(false);

  const upcoming = nextFriSatDates(8);
  const dateKey = route.date; // YYYY-MM-DD

  const load = async (date) => {
    setLoading(true);
    try {
      const q = date ? `?date=${date}` : "";
      const [r, statsR] = await Promise.all([
        api.get(`/admin/route${q}`),
        api.get(`/admin/worker-stats${q}`),
      ]);
      setRoute(r.data || { date: "", is_today: true, items: [] });
      setStats(statsR.data || { workers: [], unassigned: 0 });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { api.get("/admin/workers").then((r) => setWorkers(r.data || [])); }, []);
  useEffect(() => { load(selectedDate); /* eslint-disable-next-line */ }, [selectedDate]);

  const items = (route.items || []).filter((r) => winFilter === "all" || r.window === winFilter);
  const grouped = items.reduce((acc, r) => { (acc[r.window] = acc[r.window] || []).push(r); return acc; }, {});
  const winKeys = Object.keys(grouped).sort((a, b) => (WINDOW_TOD[a] ?? 9) - (WINDOW_TOD[b] ?? 9));

  const assignOne = async (orderId, workerId) => {
    try {
      await api.patch(`/admin/orders/${orderId}/assign-worker`, { worker_id: workerId || null });
      load(selectedDate);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not assign");
    }
  };

  const bulkAssign = async () => {
    if (!bulkWorker) return toast.error("Pick a worker.");
    setBusy(true);
    try {
      const payload = { date: dateKey, worker_id: bulkWorker, only_unassigned: false };
      if (bulkWindow !== "all") payload.window = bulkWindow;
      const r = await api.post("/admin/route/bulk-assign", payload);
      toast.success(`Assigned ${r.data.modified} pickup(s) to ${r.data.worker}`);
      load(selectedDate);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk assign failed");
    }
    setBusy(false);
  };

  const clearAll = async () => {
    if (!window.confirm("Clear ALL worker assignments for this date?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/route/clear-assignments", { date: dateKey });
      toast.success(`Cleared ${r.data.modified} assignment(s)`);
      load(selectedDate);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed");
    }
    setBusy(false);
  };

  return (
    <div data-testid="routes-tab">
      <div className="card-clay p-4 mb-4 bg-green-50 border-green-200" data-testid="routes-header">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-green-800">
              {route.is_today ? "Today's Route" : "Preview Route"}
            </div>
            <div className="font-fredoka text-2xl font-bold text-slate-900" data-testid="routes-current-date">
              {route.date || "—"}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Sorted by earliest pickup window first: 8:00 AM – 10:00 AM, then 6:00 PM – 8:00 PM.
            </div>
          </div>
          {!route.is_today && (
            <button onClick={() => setSelectedDate(null)} className="btn-push btn-push-outline px-3 py-2 text-xs font-bold" data-testid="routes-back-to-today">
              ← Back to Today&apos;s Route
            </button>
          )}
        </div>

        <div className="mt-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">View Another Date</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSelectedDate(null)} className={`rounded-xl px-3 py-2 text-xs font-semibold border-2 ${selectedDate === null ? "bg-green-500 border-green-700 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-green-400"}`} data-testid="routes-date-today">Today</button>
            {upcoming.map((d) => (
              <button key={d} onClick={() => setSelectedDate(d)} className={`rounded-xl px-3 py-2 text-xs font-semibold border-2 ${selectedDate === d ? "bg-green-500 border-green-700 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-green-400"}`} data-testid={`routes-date-${d}`}>
                {new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              </button>
            ))}
            <input type="date" value={selectedDate || ""} onChange={(e) => setSelectedDate(e.target.value || null)} className="input-clay px-3 py-2 text-xs" data-testid="routes-date-custom" />
          </div>
        </div>

        <div className="mt-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Filter by Window</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setWinFilter("all")} className={`rounded-xl px-3 py-1.5 text-xs font-semibold border-2 ${winFilter === "all" ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700"}`} data-testid="routes-win-all">All Windows</button>
            {WINDOWS.map((w) => (
              <button key={w.value} onClick={() => setWinFilter(w.value)} className={`rounded-xl px-3 py-1.5 text-xs font-semibold border-2 ${winFilter === w.value ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700"}`} data-testid={`routes-win-${w.value}`}>{w.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Per-worker progress + bulk assign */}
      <div className="card-clay p-4 mb-4" data-testid="worker-progress-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Crew Progress</div>
            <div className="font-fredoka text-lg font-bold text-slate-900">Who&apos;s doing what on {stats.date || dateKey}</div>
          </div>
          <div className="text-xs text-slate-500">
            {stats.unassigned > 0 ? <span className="text-amber-700 font-semibold">{stats.unassigned} unassigned</span> : "✅ Everyone has a job"}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {(stats.workers || []).map((w) => (
            <div key={w.worker_id} className="rounded-2xl border-2 border-slate-100 p-3" style={{ borderTopColor: w.color, borderTopWidth: 4 }} data-testid={`worker-progress-${w.worker_key}`}>
              <div className="flex items-center justify-between">
                <div className="font-fredoka font-bold text-slate-900">{w.display_name}</div>
                <div className="text-xs font-bold uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: w.color + "22", color: w.color }}>
                  {w.completed}/{w.assigned} done
                </div>
              </div>
              <div className="mt-1.5 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${w.assigned ? Math.round((w.completed / w.assigned) * 100) : 0}%`, backgroundColor: w.color }} />
              </div>
              <div className="mt-2 grid grid-cols-3 gap-1.5 text-center text-[11px]">
                <div className="bg-slate-50 rounded-lg py-1.5"><div className="font-fredoka font-bold text-base">{w.remaining}</div><div className="text-slate-500">left</div></div>
                <div className="bg-slate-50 rounded-lg py-1.5"><div className="font-fredoka font-bold text-base">{w.in_progress}</div><div className="text-slate-500">in progress</div></div>
                <div className="bg-slate-50 rounded-lg py-1.5"><div className="font-fredoka font-bold text-base">{w.avg_duration_seconds ? `${Math.round(w.avg_duration_seconds / 60)}m` : "—"}</div><div className="text-slate-500">avg / stop</div></div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-2xl bg-slate-900 text-white p-3 flex flex-wrap items-center gap-2" data-testid="bulk-assign-bar">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Bulk Assign</div>
          <select value={bulkWindow} onChange={(e) => setBulkWindow(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs" data-testid="bulk-window">
            <option value="all">All windows</option>
            {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
          <span className="text-xs">→</span>
          <select value={bulkWorker} onChange={(e) => setBulkWorker(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs" data-testid="bulk-worker">
            <option value="">Pick a worker…</option>
            {workers.filter((w) => w.active !== false).map((w) => <option key={w.id} value={w.id}>{w.display_name}</option>)}
          </select>
          <button onClick={bulkAssign} disabled={busy || !bulkWorker} className="bg-green-500 hover:bg-green-400 text-slate-900 font-bold rounded-lg px-3 py-1.5 text-xs disabled:opacity-50" data-testid="bulk-assign-btn">Assign</button>
          <button onClick={clearAll} disabled={busy} className="bg-red-500/20 hover:bg-red-500/30 text-red-200 font-bold rounded-lg px-3 py-1.5 text-xs disabled:opacity-50 ml-auto" data-testid="clear-assignments-btn">Clear all</button>
        </div>
      </div>

      {loading ? (
        <div className="card-clay p-8 text-center text-slate-500" data-testid="routes-loading">Loading route…</div>
      ) : items.length === 0 ? (
        <div className="card-clay p-8 text-center text-slate-500" data-testid="route-list-empty">No paid pickups on this date{winFilter !== "all" ? ` for this window` : ""}.</div>
      ) : (
        <div className="space-y-4" data-testid="route-list">
          {winKeys.map((wk) => {
            const wlabel = WINDOWS.find((w) => w.value === wk)?.label || wk;
            return (
              <div key={wk} className="card-clay overflow-hidden" data-testid={`route-window-${wk}`}>
                <div className="px-4 py-2 bg-slate-900 text-white flex items-center justify-between">
                  <div className="font-fredoka font-bold">{wlabel}</div>
                  <div className="text-xs font-semibold text-slate-300">{grouped[wk].length} pickup{grouped[wk].length === 1 ? "" : "s"}</div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {grouped[wk].map((r, i) => {
                    const aw = workers.find((w) => w.id === r.assigned_worker_id);
                    const dur = r.pickup_duration_seconds;
                    return (
                      <li key={r.id} className="py-3 px-4 flex items-center gap-3 flex-wrap" data-testid={`route-item-${wk}-${i}`}>
                        <div className="w-9 h-9 rounded-xl bg-green-100 text-green-700 flex items-center justify-center font-bold">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-fredoka font-bold truncate">{r.community}</div>
                          <div className="text-sm text-slate-600">Bldg {r.building} · Unit {r.unit} · {r.bags} bag(s)</div>
                          {r.location_instructions && <div className="text-xs text-slate-500 italic">{r.location_instructions}</div>}
                          {(r.pickup_started_at || dur) && (
                            <div className="text-[11px] text-slate-500 mt-1 flex gap-2 flex-wrap">
                              {r.pickup_started_at && <span>▶ {new Date(r.pickup_started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
                              {r.pickup_completed_at && <span>✅ {new Date(r.pickup_completed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
                              {dur > 0 && <span className="font-semibold">⏱ {Math.floor(dur / 60)}m {dur % 60}s</span>}
                            </div>
                          )}
                        </div>
                        <select
                          value={r.assigned_worker_id || ""}
                          onChange={(e) => assignOne(r.id, e.target.value)}
                          className="bg-white border-2 rounded-lg px-2 py-1.5 text-xs font-semibold"
                          style={aw ? { borderColor: aw.color, color: aw.color } : { borderColor: "#cbd5e1" }}
                          data-testid={`assign-${r.id}`}
                        >
                          <option value="">Unassigned</option>
                          {workers.filter((w) => w.active !== false).map((w) => <option key={w.id} value={w.id}>{w.display_name}</option>)}
                        </select>
                        <MapPin className="text-slate-400" size={18} />
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WorkersTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ display_name: "", key: "", color: "#3b82f6", sort_order: 99 });
  const load = () => api.get("/admin/workers").then((r) => { setList(r.data || []); setLoading(false); });
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim()) return toast.error("Name is required");
    try {
      await api.post("/admin/workers", form);
      setForm({ display_name: "", key: "", color: "#3b82f6", sort_order: 99 });
      toast.success("Worker added");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    }
  };

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/workers/${id}`, patch);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div data-testid="workers-tab">
      <div className="card-clay p-4 mb-4 bg-blue-50 border-blue-200 text-sm text-slate-700">
        Workers are the kids (and any future helpers) who get assigned to specific pickups. Each worker can be linked to a login so they only see their own stops. Add more here as the team grows.
      </div>

      <div className="card-clay p-4 mb-4">
        <div className="font-fredoka font-bold text-slate-900 mb-3">Add a Worker</div>
        <form onSubmit={save} className="grid sm:grid-cols-4 gap-2">
          <input type="text" placeholder="Display name (e.g. Cousin Marcus)" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="input-clay h-10 px-3 col-span-2" data-testid="worker-form-name" />
          <div className="flex gap-2 items-center">
            <label className="text-xs text-slate-500">Color</label>
            <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-14 rounded" data-testid="worker-form-color" />
          </div>
          <button type="submit" className="btn-push-green h-10 text-sm" data-testid="worker-form-submit">Add</button>
        </form>
      </div>

      <div className="space-y-2" data-testid="workers-list">
        {list.map((w) => (
          <div key={w.id} className="card-clay p-3 flex items-center gap-3 flex-wrap" data-testid={`worker-row-${w.key}`}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-fredoka font-bold text-lg" style={{ backgroundColor: w.color }}>{(w.display_name || "?").charAt(0)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-fredoka font-bold">{w.display_name}</div>
              <div className="text-xs text-slate-500">Key: {w.key} · {w.active ? "Active" : "Inactive"}{w.user_id ? " · Has login" : ""}</div>
            </div>
            <input type="color" defaultValue={w.color} onChange={(e) => update(w.id, { color: e.target.value })} className="h-9 w-12 rounded" />
            <input type="number" defaultValue={w.sort_order} onBlur={(e) => update(w.id, { sort_order: parseInt(e.target.value) || 99 })} className="input-clay h-9 px-2 w-16 text-xs" title="Sort order" />
            <button onClick={() => update(w.id, { active: !w.active })} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg ${w.active ? "bg-amber-50 text-amber-800 border-2 border-amber-200" : "bg-green-50 text-green-800 border-2 border-green-200"}`} data-testid={`worker-toggle-${w.key}`}>
              {w.active ? "Deactivate" : "Reactivate"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BirthdaySupportTab() {
  const [data, setData] = useState({ tips: [], by_sibling: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | paid | pending

  useEffect(() => {
    api.get("/admin/birthday-support").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500">Loading…</div>;

  const tips = (data.tips || []).filter((t) => filter === "all" || t.payment_status === filter);
  const sibTotals = data.by_sibling || {};
  const totalAll = Object.values(sibTotals).reduce((s, v) => s + (v.total || 0), 0);

  return (
    <div data-testid="birthday-support-tab">
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div className="card-clay p-5 bg-gradient-to-br from-green-100 to-emerald-200 border-green-300">
          <div className="text-xs font-bold uppercase tracking-wider text-green-900">Big Sib Josiah</div>
          <div className="font-fredoka text-3xl font-bold text-slate-900" data-testid="bday-total-josiah">${(sibTotals.josiah?.total || 0).toFixed(2)}</div>
          <div className="text-xs text-slate-700">{sibTotals.josiah?.count || 0} paid tip(s)</div>
        </div>
        <div className="card-clay p-5 bg-gradient-to-br from-pink-100 to-rose-200 border-pink-300">
          <div className="text-xs font-bold uppercase tracking-wider text-rose-900">Lil Sib Jayla</div>
          <div className="font-fredoka text-3xl font-bold text-slate-900" data-testid="bday-total-jayla">${(sibTotals.jayla?.total || 0).toFixed(2)}</div>
          <div className="text-xs text-slate-700">{sibTotals.jayla?.count || 0} paid tip(s)</div>
        </div>
        <div className="card-clay p-5 bg-gradient-to-br from-amber-100 to-orange-200 border-amber-300">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-900">Total received</div>
          <div className="font-fredoka text-3xl font-bold text-slate-900" data-testid="bday-total-all">${totalAll.toFixed(2)}</div>
          <div className="text-xs text-slate-700">All paid birthday support</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {["all", "paid", "pending"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 ${filter === f ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700"}`} data-testid={`bday-filter-${f}`}>
            {f === "all" ? "All" : f === "paid" ? "Paid ✅" : "Pending"}
          </button>
        ))}
      </div>

      <div className="card-clay overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100">
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Sibling</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Message</th>
            </tr>
          </thead>
          <tbody>
            {tips.map((t) => (
              <tr key={t.id} className="border-b border-slate-50" data-testid={`bday-row-${t.id}`}>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-pink-100 text-pink-800 border border-pink-200">Birthday Support</span></td>
                <td className="px-4 py-3 font-semibold">{t.sibling_label || t.sibling}</td>
                <td className="px-4 py-3 font-fredoka font-bold text-green-700">${Number(t.amount || 0).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold">{t.customer_name}</div>
                  <div className="text-xs text-slate-500">{t.customer_email}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${t.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{t.payment_status}</span>
                </td>
                <td className="px-4 py-3 max-w-xs"><div className="text-xs text-slate-700 italic truncate" title={t.message || ""}>{t.message || "—"}</div></td>
              </tr>
            ))}
            {tips.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No birthday support yet. Shoutouts auto-show 7 days before each sibling&apos;s birthday.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomersTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/customers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="customers-table">
      <div className="card-clay overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Orders</th><th className="px-4 py-3">Points</th><th className="px-4 py-3">Loyal</th><th className="px-4 py-3">Referral</th><th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-b border-slate-50">
                <td className="px-4 py-3 font-semibold">{c.name}{(c.flags || []).length > 0 && <span className="ml-2 text-xs text-red-600">🚩{c.flags.length}</span>}</td>
                <td className="px-4 py-3">{c.email}</td>
                <td className="px-4 py-3">{c.phone || "-"}</td>
                <td className="px-4 py-3">{c.order_count}</td>
                <td className="px-4 py-3">{c.dash_points}</td>
                <td className="px-4 py-3">{c.is_loyal ? "⭐" : "-"}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.referral_code}</td>
                <td className="px-4 py-3"><button onClick={() => setOpen(c.id)} className="btn-push-outline px-3 py-1 text-xs" data-testid={`customer-open-${c.id}`}>Open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <CustomerDetailModal userId={open} onClose={() => { setOpen(null); load(); }}/>}
    </div>
  );
}

function CustomerDetailModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [flagInput, setFlagInput] = useState("");
  const [flags, setFlags] = useState([]);

  useEffect(() => {
    api.get(`/admin/customer-detail/${userId}`).then((r) => {
      setData(r.data);
      setNotes(r.data.notes || "");
      setFlags(r.data.flags || []);
    });
  }, [userId]);

  const save = async () => {
    await api.put(`/admin/customers/${userId}/notes`, { notes, flags });
    toast.success("Saved");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="customer-detail-modal">
      <div className="card-clay max-w-2xl w-full p-6 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-fredoka text-2xl font-bold">{data?.user?.name || "Loading…"}</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100" data-testid="customer-close"><XIcon size={18}/></button>
        </div>
        {data && (
          <>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Info label="Email" value={data.user.email}/>
              <Info label="Phone" value={data.user.phone || "—"}/>
              <Info label="Member since" value={(data.user.created_at || "").slice(0, 10)}/>
              <Info label="Loyalty" value={data.user.is_loyal ? "⭐ Loyal" : "Regular"}/>
              <Info label="Total paid" value={`$${(data.paid_total || 0).toFixed(2)}`}/>
              <Info label="Points" value={data.user.dash_points || 0}/>
              <Info label="Referral code" value={data.user.referral_code}/>
              <Info label="Referrals made" value={data.referrals_made || 0}/>
            </div>

            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Flags</div>
              <div className="flex flex-wrap gap-2 mb-2" data-testid="customer-flags">
                {flags.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-bold">
                    🚩 {f}
                    <button onClick={() => setFlags(flags.filter((_, idx) => idx !== i))} className="ml-1 text-red-900">×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input className="input-clay h-10 px-3 flex-1" placeholder="e.g. dog at door · bags often late" value={flagInput} onChange={(e) => setFlagInput(e.target.value)} data-testid="customer-flag-input"/>
                <button onClick={() => { if (flagInput.trim()) { setFlags([...flags, flagInput.trim()]); setFlagInput(""); } }} className="btn-push-outline px-3 py-1 text-xs" data-testid="customer-flag-add">Add flag</button>
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Internal notes</div>
              <textarea rows={4} className="input-clay px-3 py-2 w-full" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="customer-notes"/>
            </div>

            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Recent orders ({data.orders.length})</div>
              <ul className="text-xs text-slate-700 space-y-1 max-h-40 overflow-auto">
                {data.orders.slice(0, 10).map((o) => (
                  <li key={o.id} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-slate-50">
                    <span>{(o.pickup_date || o.created_at).slice(0, 10)} · {windowLabel(o.window)} · {o.bags} bag(s)</span>
                    <span className="font-bold">${o.total_amount.toFixed(2)} <span className="text-slate-500">{o.payment_status}</span></span>
                  </li>
                ))}
              </ul>
            </div>

            <button onClick={save} className="btn-push-green w-full mt-5 py-3" data-testid="customer-save">Save changes</button>
          </>
        )}
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="rounded-xl bg-slate-50 p-3">
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-semibold text-slate-800 mt-0.5">{value}</div>
  </div>
);

function RunSheetTab() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [window, setWindow] = useState("");
  const [data, setData] = useState({ stops: [], count: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const q = `date=${date}${window ? `&window=${window}` : ""}`;
    const { data } = await api.get(`/admin/run-sheet?${q}`);
    setData(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, window]);

  const complete = async (id) => {
    await api.post(`/admin/orders/${id}/quick-complete`, {});
    toast.success("Marked complete");
    load();
  };

  const gmapsLink = (s) => {
    const q = encodeURIComponent(`${s.community} ${s.building} ${s.unit}`);
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  };

  return (
    <div data-testid="runsheet-tab">
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <Field label="Date"><input type="date" className="input-clay h-10 px-3" value={date} onChange={(e) => setDate(e.target.value)} data-testid="runsheet-date"/></Field>
        <Field label="Window">
          <select className="input-clay h-10 px-3" value={window} onChange={(e) => setWindow(e.target.value)} data-testid="runsheet-window">
            <option value="">All windows</option>
            {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </Field>
        <div className="text-sm text-slate-500 ml-auto">Total stops: <b>{data.count}</b></div>
      </div>
      {loading ? <div className="text-slate-500">Loading…</div> : (
        <div className="card-clay overflow-hidden" data-testid="runsheet-list">
          <ul className="divide-y divide-slate-100">
            {data.stops.length === 0 && <li className="p-8 text-center text-slate-400">No stops on this day.</li>}
            {data.stops.map((s, i) => {
              const skipped = s.status === "skipped" || s.status === "canceled";
              const done = s.status === "picked_up" || s.status === "completed";
              return (
                <li key={s.id} className={`flex items-center gap-3 p-4 ${skipped ? "opacity-60 line-through" : ""} ${done ? "bg-green-50" : ""}`} data-testid={`runsheet-stop-${i}`}>
                  <button onClick={() => !done && complete(s.id)} className={`w-8 h-8 rounded-xl border-2 ${done ? "bg-green-500 border-green-600" : "bg-white border-slate-300 hover:border-green-500"} flex items-center justify-center flex-shrink-0`} data-testid={`runsheet-toggle-${i}`} disabled={done || skipped}>
                    {done && <CheckCircle size={18} className="text-white"/>}
                  </button>
                  <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-700 font-fredoka font-bold flex items-center justify-center text-sm flex-shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-fredoka font-bold text-slate-900 truncate">{s.name} · {s.community}</div>
                    <div className="text-xs text-slate-500">Bldg {s.building} · Unit {s.unit} · {s.bags} bag(s){s.monthly_cleaning ? " + clean" : ""}</div>
                    {s.location_instructions && <div className="text-[11px] text-slate-500 italic mt-0.5">{s.location_instructions}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      s.payment_status === "paid" ? "bg-green-100 text-green-800" :
                      s.payment_status === "pay_in_person" ? "bg-blue-100 text-blue-800" :
                      "bg-red-100 text-red-800"
                    }`}>
                      {s.payment_status === "paid" ? "Paid ✅" : s.payment_status === "pay_in_person" ? "In Person 🤝" : "Owes 🔴"}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      done ? "bg-green-100 text-green-800" :
                      skipped ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-800"
                    }`}>
                      {done ? "Completed ✅" : s.status === "skipped" ? "Skip ⏭️" : s.status === "canceled" ? "Cancelled ❌" : "Pending 🕐"}
                    </span>
                    <a href={gmapsLink(s)} target="_blank" rel="noreferrer" className="text-xs font-bold text-green-700 inline-flex items-center gap-1" data-testid={`runsheet-gmaps-${i}`}>
                      Maps <ExternalLink size={10}/>
                    </a>
                    {s.photo_path && <PhotoLink path={s.photo_path}/>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function PhotoLink({ path }) {
  const [open, setOpen] = useState(false);
  const url = `${BACKEND_URL}/api/files/photo?path=${encodeURIComponent(path)}`;
  const token = localStorage.getItem("bd_token");
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-xs font-bold text-blue-700 inline-flex items-center gap-1" data-testid="photo-view"><Camera size={10}/> Photo</button>
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <img src={`${url}&_t=${token}`} alt="Pickup proof" className="max-w-full max-h-[90vh] rounded-2xl" onError={(e) => { e.target.alt = "Photo (auth header needed for view)"; }}/>
        </div>
      )}
    </>
  );
}

function MoneyTab() {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  const [resending, setResending] = useState({});
  const load = async () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    const { data } = await api.get(`/admin/money-tracker${q}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    api.get("/admin/money/today").then((r) => setToday(r.data)).catch(() => setToday(null));
  }, []);

  const resendReceipt = async (orderId) => {
    setResending((p) => ({ ...p, [orderId]: true }));
    try {
      await api.post(`/admin/orders/${orderId}/resend-receipt`);
      toast.success("Receipt resent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send receipt");
    }
    setResending((p) => ({ ...p, [orderId]: false }));
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("bd_token");
    const res = await fetch(`${BACKEND_URL}/api/admin/orders/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "earnings.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div data-testid="money-tab">
      {today && (
        <div className="card-clay p-5 mb-4 bg-slate-900 text-white" data-testid="money-today">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Today&apos;s reconciliation</div>
              <div className="font-fredoka text-2xl font-bold">{today.date}</div>
            </div>
            <div className="text-xs text-slate-400">Stripe fees est. 2.9% + $0.30/txn</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Gross (Stripe)</div>
              <div className="font-fredoka text-2xl font-bold mt-1" data-testid="today-gross-stripe">${today.gross_stripe.toFixed(2)}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{today.stripe_txn_count} txn</div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Est. Stripe fees</div>
              <div className="font-fredoka text-2xl font-bold mt-1 text-red-300" data-testid="today-fees">-${today.stripe_fees_est.toFixed(2)}</div>
            </div>
            <div className="rounded-2xl bg-amber-400/15 border border-amber-300/30 p-3">
              <div className="text-[10px] uppercase font-bold tracking-wider text-amber-300">Est. Net (today)</div>
              <div className="font-fredoka text-2xl font-bold mt-1 text-amber-200" data-testid="today-net">${today.net_est.toFixed(2)}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">incl. ${today.gross_in_person.toFixed(2)} in-person</div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Tips today</div>
              <div className="font-fredoka text-2xl font-bold mt-1" data-testid="today-tips">${today.tips_today.toFixed(2)}</div>
            </div>
          </div>

          {today.recent.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Recent transactions</div>
              <div className="space-y-1.5">
                {today.recent.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid={`recent-txn-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold">{r.name || "—"}</div>
                      <div className="text-[10px] text-slate-400">{r.community} · {r.method === "in_person" ? "in person 🤝" : "stripe 💳"} · {r.order_type}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-fredoka font-bold">${r.amount.toFixed(2)}</div>
                      {r.tip > 0 && <div className="text-[10px] text-amber-300">+${r.tip.toFixed(2)} tip</div>}
                    </div>
                    <button onClick={() => resendReceipt(r.id)} disabled={!!resending[r.id]} className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 border border-white/10 disabled:opacity-50" data-testid={`resend-receipt-${r.id}`}>
                      {resending[r.id] ? "…" : "Resend ✉️"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="card-clay p-5 bg-green-50 border-green-200">
          <div className="text-xs font-bold uppercase tracking-wider text-green-800">This week (paid)</div>
          <div className="font-fredoka text-4xl font-bold text-slate-900" data-testid="money-weekly">${data.weekly_paid.toFixed(2)}</div>
        </div>
        <div className="card-clay p-5 bg-amber-50 border-amber-200">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-800">This month (paid)</div>
          <div className="font-fredoka text-4xl font-bold text-slate-900" data-testid="money-monthly">${data.monthly_paid.toFixed(2)}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {[["all", "All"], ["paid", "Paid ✅"], ["owes", "Still Owes 🔴"], ["in_person", "In Person 🤝"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`btn-push ${filter === k ? "btn-push-green" : "btn-push-outline"} px-4 py-2 text-sm`} data-testid={`money-filter-${k}`}>{l}</button>
        ))}
        <button onClick={exportCsv} className="ml-auto btn-push-outline px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="money-export"><Download size={14}/> Export CSV</button>
      </div>

      <div className="card-clay overflow-x-auto" data-testid="money-list">
        <table className="min-w-full text-sm">
          <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100"><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Community</th><th className="px-4 py-3">Orders</th><th className="px-4 py-3">Paid</th><th className="px-4 py-3">Owes</th><th className="px-4 py-3">In Person</th></tr></thead>
          <tbody>
            {data.customers.map((c) => (
              <tr key={c.email} className="border-b border-slate-50">
                <td className="px-4 py-3 font-semibold">{c.name || "—"}<div className="text-xs text-slate-500">{c.email}</div></td>
                <td className="px-4 py-3">{c.community}</td>
                <td className="px-4 py-3">{c.orders}</td>
                <td className="px-4 py-3 text-green-700 font-semibold">${c.paid.toFixed(2)}</td>
                <td className="px-4 py-3 text-red-700 font-semibold">{c.owes > 0 ? `$${c.owes.toFixed(2)}` : "—"}</td>
                <td className="px-4 py-3 text-blue-700 font-semibold">{c.in_person > 0 ? `$${c.in_person.toFixed(2)}` : "—"}</td>
              </tr>
            ))}
            {data.customers.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No customers in this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubscriptionsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/stripe-subscriptions").then((r) => { setList(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-slate-500">Loading…</div>;
  return (
    <div data-testid="subscriptions-tab">
      <div className="card-clay p-4 mb-3 bg-blue-50 border-blue-200 text-sm text-slate-700">
        Weekly recurring Stripe subscriptions. Records get created automatically after the first invoice is paid.
        Auto-generated weekly pickup orders are tagged with <code className="px-1 py-0.5 bg-white border rounded">auto_generated</code> in the Orders table.
      </div>
      <div className="card-clay overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100">
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Bags</th>
              <th className="px-4 py-3">Window</th>
              <th className="px-4 py-3">Community</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last invoice</th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-b border-slate-50" data-testid={`sub-row-${s.id}`}>
                <td className="px-4 py-3 font-semibold">{s.user_email || "—"}</td>
                <td className="px-4 py-3">{s.bags}</td>
                <td className="px-4 py-3">{windowLabel(s.window)}</td>
                <td className="px-4 py-3">{s.community} · {s.building}/{s.unit}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${s.status === "active" ? "bg-green-100 text-green-800" : s.status === "canceled" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700"}`}>
                    {s.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{s.last_invoice_at ? new Date(s.last_invoice_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No Stripe subscriptions yet. They appear here after a customer completes a weekly checkout.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReferralsTab() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/referrals").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, status) => {
    await api.put(`/admin/referrals/${id}`, { status });
    toast.success(`Referral ${status}`);
    load();
  };
  return (
    <div className="card-clay overflow-x-auto" data-testid="referrals-table">
      <table className="min-w-full text-sm">
        <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Referrer</th><th className="px-4 py-3">Referee</th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.id} className="border-b border-slate-50">
              <td className="px-4 py-3">{new Date(r.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-3 text-xs">{r.referrer_id.slice(0, 8)}</td>
              <td className="px-4 py-3 text-xs">{r.referee_id.slice(0, 8)}</td>
              <td className="px-4 py-3 text-xs">{r.order_id.slice(0, 8)}</td>
              <td className="px-4 py-3">{r.status}</td>
              <td className="px-4 py-3">
                <div className="flex gap-1">
                  <button onClick={() => act(r.id, "approved")} className="btn-push-green px-2 py-1 text-xs" data-testid={`approve-${r.id}`}>Approve</button>
                  <button onClick={() => act(r.id, "rejected")} className="btn-push-outline px-2 py-1 text-xs" data-testid={`reject-${r.id}`}>Reject</button>
                </div>
              </td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No referrals yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newBlackout, setNewBlackout] = useState("");

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);

  if (!s) return <div className="text-slate-500">Loading…</div>;

  const upd = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="card-clay p-5">
        <h3 className="font-fredoka font-bold text-lg mb-3 flex items-center gap-2"><SettingsIcon size={18}/> Pricing</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <NumField label="1 bag" value={s.price_1_bag} onChange={(v) => upd("price_1_bag", v)} testid="price-1"/>
          <NumField label="2 bags" value={s.price_2_bags} onChange={(v) => upd("price_2_bags", v)} testid="price-2"/>
          <NumField label="Extra bag" value={s.price_extra_bag} onChange={(v) => upd("price_extra_bag", v)} testid="price-extra"/>
          <NumField label="Monthly cleaning" value={s.monthly_cleaning_price} onChange={(v) => upd("monthly_cleaning_price", v)} testid="price-cleaning"/>
        </div>
      </div>

      <div className="card-clay p-5">
        <h3 className="font-fredoka font-bold text-lg mb-3">Capacity per window</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <NumField label="Friday AM" value={s.capacity_fri_am} onChange={(v) => upd("capacity_fri_am", v)} integer testid="cap-friam"/>
          <NumField label="Friday PM" value={s.capacity_fri_pm} onChange={(v) => upd("capacity_fri_pm", v)} integer testid="cap-fripm"/>
          <NumField label="Saturday AM" value={s.capacity_sat_am} onChange={(v) => upd("capacity_sat_am", v)} integer testid="cap-satam"/>
          <NumField label="Saturday PM" value={s.capacity_sat_pm} onChange={(v) => upd("capacity_sat_pm", v)} integer testid="cap-satpm"/>
        </div>
        <p className="text-xs text-slate-500 mt-2">Set 0 to fully disable a window. Customers will see it greyed out at checkout.</p>
      </div>

      <div className="card-clay p-5">
        <h3 className="font-fredoka font-bold text-lg mb-3">Service availability</h3>
        <label className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border-2 border-slate-200 cursor-pointer" data-testid="toggle-booking">
          <input type="checkbox" checked={s.booking_enabled} onChange={(e) => upd("booking_enabled", e.target.checked)} className="w-5 h-5 accent-green-500"/>
          <span className="font-semibold text-slate-800">Bookings are open</span>
        </label>
        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Blackout dates</div>
          <div className="flex gap-2 mb-2">
            <input type="date" className="input-clay h-10 px-3" value={newBlackout} onChange={(e) => setNewBlackout(e.target.value)} data-testid="blackout-date-input"/>
            <button onClick={() => { if (newBlackout && !s.blackout_dates.includes(newBlackout)) { upd("blackout_dates", [...s.blackout_dates, newBlackout]); setNewBlackout(""); } }} className="btn-push-outline px-3 py-2 text-sm" data-testid="add-blackout">Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {s.blackout_dates.map((d) => (
              <span key={d} className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-bold">
                {d}
                <button onClick={() => upd("blackout_dates", s.blackout_dates.filter((x) => x !== d))} className="text-amber-900">×</button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} className="btn-push-green px-6 py-3 inline-flex items-center gap-2" data-testid="save-settings">
        <Save size={16}/> {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="inline-flex items-center gap-2 text-sm">
    <span className="text-slate-500 font-semibold">{label}</span>
    <select className="input-clay h-9 px-2" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const NumField = ({ label, value, onChange, integer, testid }) => (
  <label className="block">
    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <input type="number" step={integer ? "1" : "0.01"} min="0" className="input-clay h-10 px-3 w-full" value={value} onChange={(e) => onChange(integer ? parseInt(e.target.value || 0) : parseFloat(e.target.value || 0))} data-testid={testid}/>
  </label>
);

const ActionBtn = ({ children, onClick, testid }) => (
  <button onClick={onClick} className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700" data-testid={testid}>{children}</button>
);

function StaffTab({ actorRole }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", phone: "", role: "kid_worker", password: "" });
  const [creating, setCreating] = useState(false);

  const isParent = actorRole === "parent";
  const ROLE_OPTIONS = isParent
    ? [["kid_worker", "Kid Worker"], ["admin", "Admin"], ["superadmin", "Superadmin"], ["parent", "Parent"]]
    : [["kid_worker", "Kid Worker"], ["admin", "Admin"], ["superadmin", "Superadmin"]];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/staff");
      setList(data);
    } catch (e) { toast.error("Could not load staff"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email || form.password.length < 6) return toast.error("Email + 6-char password required");
    setCreating(true);
    try {
      await api.post("/admin/staff", form);
      toast.success(`Created ${form.role} account`);
      setForm({ email: "", name: "", phone: "", role: "kid_worker", password: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setCreating(false);
  };

  const updateRole = async (id, role) => {
    try {
      await api.put(`/admin/staff/${id}`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/staff/${s.id}`, { active: !s.active });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resetPassword = async (s) => {
    const p = window.prompt(`Set new password for ${s.email} (min 6 chars):`);
    if (!p) return;
    if (p.length < 6) return toast.error("Min 6 chars");
    try {
      await api.post(`/admin/staff/${s.id}/reset-password`, { password: p });
      toast.success("Password reset");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deactivate = async (s) => {
    if (!window.confirm(`Deactivate ${s.email}?`)) return;
    try {
      await api.delete(`/admin/staff/${s.id}`);
      toast.success("Deactivated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const roleBadge = (r) => ({
    parent: "bg-purple-100 text-purple-800",
    superadmin: "bg-amber-100 text-amber-800",
    admin: "bg-blue-100 text-blue-800",
    kid_worker: "bg-green-100 text-green-800",
  }[r] || "bg-slate-100 text-slate-700");

  return (
    <div className="space-y-5" data-testid="staff-tab">
      <div className="card-clay p-5 bg-purple-50 border-purple-200 flex items-start gap-3">
        <Shield className="text-purple-700 flex-shrink-0" strokeWidth={2.5}/>
        <div className="text-sm text-purple-900">
          <div className="font-fredoka font-bold">Staff management</div>
          <div className="text-xs mt-1">Signed in as <b className="capitalize">{actorRole}</b>. {isParent ? "You can manage everyone." : "You can manage admins, superadmins, and kid workers (but not parent accounts)."}</div>
        </div>
      </div>

      <form onSubmit={create} className="card-clay p-5" data-testid="staff-create-form">
        <div className="flex items-center gap-2 mb-3"><UserPlus size={18}/><h3 className="font-fredoka font-bold text-lg">Add a staff member</h3></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Email"><input type="email" required className="input-clay h-11 px-3 w-full" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="staff-email"/></Field>
          <Field label="Full name"><input className="input-clay h-11 px-3 w-full" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="staff-name"/></Field>
          <Field label="Phone (optional)"><input className="input-clay h-11 px-3 w-full" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="staff-phone"/></Field>
          <Field label="Role">
            <select className="input-clay h-11 px-3 w-full" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="staff-role">
              {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Temporary password (min 6)"><input type="text" required minLength={6} className="input-clay h-11 px-3 w-full" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="staff-password"/></Field>
        </div>
        <button disabled={creating} className="btn-push-green mt-4 px-5 py-2.5 inline-flex items-center gap-2" data-testid="staff-create-btn">
          <UserPlus size={16}/> {creating ? "Creating…" : "Create account"}
        </button>
      </form>

      <div className="card-clay overflow-x-auto" data-testid="staff-list">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-slate-400">Loading…</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-slate-400">No staff yet.</td></tr>
            ) : list.map((s) => {
              const isParentRow = s.role === "parent";
              const canEdit = isParent || (actorRole === "superadmin" && !isParentRow);
              return (
                <tr key={s.id} className="border-b border-slate-50" data-testid={`staff-row-${s.id}`}>
                  <td className="px-4 py-3 font-semibold">{s.name || "—"}</td>
                  <td className="px-4 py-3">{s.email}</td>
                  <td className="px-4 py-3">
                    {canEdit && !isParentRow ? (
                      <select value={s.role} onChange={(e) => updateRole(s.id, e.target.value)} className={`input-clay h-8 px-2 text-xs font-bold uppercase tracking-wider ${roleBadge(s.role)}`} data-testid={`staff-role-${s.id}`}>
                        {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    ) : (
                      <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full ${roleBadge(s.role)}`}>{s.role.replace("_", " ")}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${s.active === false ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                      {s.active === false ? "Inactive" : "Active"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {canEdit && (
                      <div className="flex gap-1 flex-wrap">
                        <ActionBtn onClick={() => resetPassword(s)} testid={`staff-reset-${s.id}`}><KeyRound size={12} className="inline mr-1"/>Reset PW</ActionBtn>
                        <ActionBtn onClick={() => toggleActive(s)} testid={`staff-toggle-${s.id}`}>{s.active === false ? "Reactivate" : "Deactivate"}</ActionBtn>
                        {!isParentRow && (
                          <ActionBtn onClick={() => deactivate(s)} testid={`staff-delete-${s.id}`}>Delete</ActionBtn>
                        )}
                      </div>
                    )}
                    {!canEdit && <span className="text-xs text-slate-400">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);

function ContestsTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({
    title: "Refer-a-Neighbor Contest",
    prize: "Free month of service",
    start_at: new Date().toISOString().slice(0, 10),
    end_at: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    active: true,
  });
  const load = () => api.get("/admin/contests").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      start_at: new Date(form.start_at + "T00:00:00Z").toISOString(),
      end_at: new Date(form.end_at + "T23:59:59Z").toISOString(),
    };
    await api.post("/admin/contests", payload);
    toast.success("Contest created");
    load();
  };
  const toggle = async (c) => { await api.put(`/admin/contests/${c.id}`, { active: !c.active }); load(); };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/contests/${id}`); load(); } };

  return (
    <div className="space-y-4">
      <form onSubmit={create} className="card-clay p-5 space-y-3" data-testid="contest-form">
        <h3 className="font-fredoka font-bold text-lg">Create a contest</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Title"><input className="input-clay h-11 px-3 w-full" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="contest-title-input"/></Field>
          <Field label="Prize"><input className="input-clay h-11 px-3 w-full" value={form.prize} onChange={(e) => setForm({...form, prize: e.target.value})} data-testid="contest-prize-input"/></Field>
          <Field label="Start date"><input type="date" className="input-clay h-11 px-3 w-full" value={form.start_at} onChange={(e) => setForm({...form, start_at: e.target.value})} data-testid="contest-start"/></Field>
          <Field label="End date"><input type="date" className="input-clay h-11 px-3 w-full" value={form.end_at} onChange={(e) => setForm({...form, end_at: e.target.value})} data-testid="contest-end"/></Field>
        </div>
        <button className="btn-push-green px-5 py-2.5" data-testid="contest-create">Create</button>
      </form>
      <div className="space-y-2" data-testid="contests-list">
        {list.map((c) => (
          <div key={c.id} className="card-clay p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`contest-${c.id}`}>
            <div>
              <div className="font-fredoka font-bold">{c.title}</div>
              <div className="text-xs text-slate-500">{c.start_at.slice(0,10)} → {c.end_at.slice(0,10)} · {c.prize}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggle(c)} className={`btn-push ${c.active ? "btn-push-green" : "btn-push-outline"} px-3 py-1.5 text-xs`} data-testid={`contest-toggle-${c.id}`}>{c.active ? "Active" : "Inactive"}</button>
              <button onClick={() => del(c.id)} className="btn-push-outline px-3 py-1.5 text-xs text-red-600" data-testid={`contest-del-${c.id}`}>Delete</button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="text-slate-400 text-center py-6">No contests yet.</div>}
      </div>
    </div>
  );
}

function MysteryTab() {
  const [list, setList] = useState([]);
  const [key, setKey] = useState("");
  const REWARD_KEYS = [
    ["", "Random reward"],
    ["free_bag_upgrade", "Free bag upgrade"],
    ["double_points_week", "Double points week"],
    ["ten_off_cleaning", "10% off bin cleaning"],
  ];
  const load = () => api.get("/admin/mystery-rewards").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const drop = async () => {
    if (!window.confirm("Drop a mystery reward to 1–3 random customers now?")) return;
    try {
      const { data } = await api.post("/admin/mystery-rewards/drop", { key: key || undefined });
      toast.success(`Dropped to ${data.dropped} customer(s)`);
      load();
    } catch { toast.error("Drop failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="card-clay p-5" data-testid="mystery-control">
        <h3 className="font-fredoka font-bold text-lg">Drop a mystery reward</h3>
        <p className="text-sm text-slate-500 mb-3">Picks 1–3 random customers and gifts them a surprise.</p>
        <div className="flex flex-wrap gap-2 items-end">
          <Field label="Reward">
            <select className="input-clay h-11 px-3" value={key} onChange={(e) => setKey(e.target.value)} data-testid="mystery-key">
              {REWARD_KEYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <button onClick={drop} className="btn-push-amber px-5 py-2.5" data-testid="mystery-drop">🎁 Drop now</button>
        </div>
      </div>
      <div className="card-clay overflow-x-auto" data-testid="mystery-list">
        <table className="min-w-full text-sm">
          <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100"><th className="px-4 py-3">When</th><th className="px-4 py-3">Reward</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Claimed</th></tr></thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.id} className="border-b border-slate-50">
                <td className="px-4 py-3">{new Date(m.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 font-semibold">{m.title}</td>
                <td className="px-4 py-3 font-mono text-xs">{(m.user_id || "").slice(0,8)}</td>
                <td className="px-4 py-3">{m.claimed ? "✅" : "—"}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-400">No drops yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnnouncementsTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ message: "", emoji: "📣", tone: "info", active: true });
  const load = () => api.get("/admin/announcements").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.message) return;
    await api.post("/admin/announcements", form);
    setForm({ message: "", emoji: "📣", tone: "info", active: true });
    toast.success("Announcement added");
    load();
  };
  const toggle = async (a) => { await api.put(`/admin/announcements/${a.id}`, { active: !a.active }); load(); };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/announcements/${id}`); load(); } };

  return (
    <div className="space-y-4">
      <form onSubmit={create} className="card-clay p-5 grid sm:grid-cols-[80px_1fr_120px_120px] gap-2 items-end" data-testid="ann-form">
        <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Emoji</div><input className="input-clay h-10 px-3 w-full" value={form.emoji} onChange={(e) => setForm({...form, emoji: e.target.value})} data-testid="ann-emoji"/></label>
        <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Message</div><input className="input-clay h-10 px-3 w-full" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} data-testid="ann-message"/></label>
        <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Tone</div><select className="input-clay h-10 px-2 w-full" value={form.tone} onChange={(e) => setForm({...form, tone: e.target.value})} data-testid="ann-tone"><option value="info">Info</option><option value="warn">Warn</option><option value="success">Success</option></select></label>
        <button className="btn-push-green py-2.5 px-4" data-testid="ann-add">Add</button>
      </form>
      <div className="space-y-2" data-testid="ann-list">
        {list.map((a) => (
          <div key={a.id} className="card-clay p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`ann-${a.id}`}>
            <div className="flex items-center gap-3"><span className="text-2xl">{a.emoji}</span><div><div className="font-semibold">{a.message}</div><div className="text-xs text-slate-500 uppercase font-bold tracking-wider">{a.tone}</div></div></div>
            <div className="flex gap-2">
              <button onClick={() => toggle(a)} className={`btn-push ${a.active ? "btn-push-green" : "btn-push-outline"} px-3 py-1.5 text-xs`} data-testid={`ann-toggle-${a.id}`}>{a.active ? "Active" : "Inactive"}</button>
              <button onClick={() => del(a.id)} className="btn-push-outline px-3 py-1.5 text-xs text-red-600" data-testid={`ann-del-${a.id}`}>Delete</button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="text-slate-400 text-center py-6">No announcements</div>}
      </div>
    </div>
  );
}

function AreasTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", status: "coming_soon", blurb: "" });
  const load = () => api.get("/service-areas").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); if (!form.name) return; await api.post("/admin/service-areas", form); setForm({ name: "", status: "coming_soon", blurb: "" }); load(); };
  const upd = async (id, patch) => { await api.put(`/admin/service-areas/${id}`, patch); load(); };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/service-areas/${id}`); load(); } };

  return (
    <div className="space-y-4">
      <form onSubmit={create} className="card-clay p-5 grid sm:grid-cols-[1fr_140px_2fr_100px] gap-2 items-end" data-testid="area-form">
        <input className="input-clay h-10 px-3" placeholder="Area name (e.g. Hawthorne)" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="area-name"/>
        <select className="input-clay h-10 px-2" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} data-testid="area-status"><option value="active">Active</option><option value="coming_soon">Coming soon</option></select>
        <input className="input-clay h-10 px-3" placeholder="Short blurb" value={form.blurb} onChange={(e) => setForm({...form, blurb: e.target.value})} data-testid="area-blurb"/>
        <button className="btn-push-green py-2.5" data-testid="area-add">Add</button>
      </form>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="area-list">
        {list.map((a) => (
          <div key={a.id} className="card-clay p-4" data-testid={`area-${a.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-fredoka font-bold text-lg">{a.name}</div>
              <select value={a.status} onChange={(e) => upd(a.id, { status: e.target.value })} className="input-clay h-8 px-2 text-xs" data-testid={`area-status-${a.id}`}><option value="active">Active</option><option value="coming_soon">Coming soon</option></select>
            </div>
            <div className="text-xs text-slate-500 mt-1">{a.blurb}</div>
            <button onClick={() => del(a.id)} className="btn-push-outline mt-3 px-3 py-1 text-xs text-red-600" data-testid={`area-del-${a.id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewsTab() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/reviews").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, approved) => { await api.put(`/admin/reviews/${id}`, { approved }); load(); };
  return (
    <div className="space-y-3" data-testid="admin-reviews-list">
      {list.map((r) => (
        <div key={r.id} className="card-clay p-5 flex items-start justify-between gap-3 flex-wrap" data-testid={`adm-review-${r.id}`}>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className={i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}/>)}
            </div>
            <p className="text-sm text-slate-700 mt-1">&quot;{r.comment || "No comment"}&quot;</p>
            <div className="text-xs text-slate-500 mt-1">{r.user_name} · {r.community} · {new Date(r.created_at).toLocaleDateString()}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => act(r.id, true)} className={`btn-push ${r.approved ? "btn-push-green" : "btn-push-outline"} px-3 py-1.5 text-xs`} data-testid={`adm-review-approve-${r.id}`}>{r.approved ? "Approved" : "Approve"}</button>
            <button onClick={() => act(r.id, false)} className="btn-push-outline px-3 py-1.5 text-xs text-red-600" data-testid={`adm-review-reject-${r.id}`}>Reject</button>
          </div>
        </div>
      ))}
      {list.length === 0 && <div className="text-slate-400 text-center py-6">No reviews yet</div>}
    </div>
  );
}

function ContactsTab() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/contacts").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (c) => { await api.put(`/admin/contacts/${c.id}`, { handled: !c.handled }); load(); };
  return (
    <div className="space-y-3" data-testid="admin-contacts-list">
      {list.map((c) => (
        <div key={c.id} className={`card-clay p-5 ${c.handled ? "opacity-70" : ""}`} data-testid={`adm-contact-${c.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-fredoka font-bold">{c.name || "Anonymous"}</div>
              <div className="text-xs text-slate-500">{c.email} · {new Date(c.created_at).toLocaleString()}</div>
            </div>
            <button onClick={() => toggle(c)} className={`btn-push ${c.handled ? "btn-push-outline" : "btn-push-green"} px-3 py-1.5 text-xs`} data-testid={`adm-contact-toggle-${c.id}`}>{c.handled ? "Handled ✓" : "Mark handled"}</button>
          </div>
          <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{c.message}</p>
        </div>
      ))}
      {list.length === 0 && <div className="text-slate-400 text-center py-6">Inbox is empty</div>}
    </div>
  );
}

function WaitlistTab() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/waitlist").then((r) => setList(r.data)); }, []);
  return (
    <div className="card-clay overflow-x-auto" data-testid="admin-waitlist">
      <table className="min-w-full text-sm">
        <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b-2 border-slate-100"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Area</th></tr></thead>
        <tbody>
          {list.map((w) => (
            <tr key={w.id} className="border-b border-slate-50">
              <td className="px-4 py-3">{new Date(w.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-3">{w.name || "-"}</td>
              <td className="px-4 py-3 font-semibold">{w.email}</td>
              <td className="px-4 py-3">{w.area}</td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-400">No waitlist signups</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function BroadcastTab() {
  const [form, setForm] = useState({ title: "📣 Update from Bag Dash Kids", body: "", community: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const send = async (e) => {
    e.preventDefault();
    if (!form.body) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications/broadcast", form);
      toast.success(`Sent to ${data.sent} customer(s)`);
      setResult(data.sent);
      setForm({ ...form, body: "" });
    } catch { toast.error("Failed"); }
    setSending(false);
  };
  return (
    <form onSubmit={send} className="card-clay p-6 space-y-3 max-w-2xl" data-testid="broadcast-form">
      <h3 className="font-fredoka font-bold text-lg flex items-center gap-2"><Megaphone size={20}/> Broadcast notification</h3>
      <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Title</div><input className="input-clay h-11 px-3 w-full" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="bc-title"/></label>
      <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Message</div><textarea rows={4} className="input-clay px-3 py-2 w-full" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} data-testid="bc-body"/></label>
      <label className="block"><div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Target community (optional)</div><input className="input-clay h-11 px-3 w-full" placeholder="Hawthorne (leave blank for all)" value={form.community} onChange={(e) => setForm({...form, community: e.target.value})} data-testid="bc-community"/></label>
      <button disabled={sending} className="btn-push-green px-5 py-2.5 inline-flex items-center gap-2" data-testid="bc-send"><Send size={14}/> {sending ? "Sending…" : "Send"}</button>
      {result !== null && <div className="text-sm text-green-700 font-bold">Sent to {result} customer(s).</div>}
    </form>
  );
}
